import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { t } from "../lib/i18n";
import { Bell, Check, X } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function NotificationsBell() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const ref = useRef(null);

  const load = () => {
    if (!user || user.role !== "player") return;
    api.get("/me/notifications").then((r) => setItems(r.data)).catch(() => {});
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  if (!user || user.role !== "player") return null;

  const pending = items.filter((n) => n.status === "pending");

  const act = async (id, action) => {
    try {
      await api.post(`/me/notifications/${id}/${action}`);
      toast.success(action === "accept" ? t.inviteAccepted : t.inviteDeclined);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || t.failed);
    }
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="notif-bell"
        className="relative p-2 text-white/70 hover:text-white"
        title={t.notifications}
      >
        <Bell size={18} weight={pending.length > 0 ? "fill" : "regular"} />
        {pending.length > 0 && (
          <span
            data-testid="notif-badge"
            className="absolute top-1 right-1 w-2 h-2 rounded-full bg-[#FF0055] animate-pulse"
          />
        )}
      </button>
      {open && (
        <div
          data-testid="notif-panel"
          className="absolute right-0 mt-2 w-80 sm:w-96 border border-white/15 bg-[#0C0C14] shadow-2xl z-50 max-h-[480px] overflow-y-auto"
        >
          <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#00E5FF]">
              {t.notifications}
            </div>
            <span className="text-[10px] font-mono text-white/40">
              {pending.length} {t.pending.toLowerCase()}
            </span>
          </div>
          {items.length === 0 ? (
            <div className="px-4 py-12 text-center text-white/40 font-mono text-xs">
              {t.noNotifications}
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {items.map((n) => (
                <div key={n.id} data-testid={`notif-${n.id}`} className="px-4 py-3">
                  <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#FF0055]">
                    {t.tournamentInvite}
                  </div>
                  <div className="text-sm font-semibold mt-1">
                    {n.tournament_name || "—"}
                  </div>
                  <div className="text-[10px] text-white/40 font-mono mt-1">
                    {n.sender_handle || "—"} · {new Date(n.created_at).toLocaleDateString("pl-PL")}
                  </div>
                  {n.status === "pending" ? (
                    <div className="flex gap-2 mt-3">
                      <button
                        onClick={() => act(n.id, "accept")}
                        data-testid={`accept-${n.id}`}
                        className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-1.5 bg-[#00E5FF]/15 border border-[#00E5FF]/40 text-[#00E5FF] text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-[#00E5FF]/25"
                      >
                        <Check size={12} weight="bold" /> {t.accept}
                      </button>
                      <button
                        onClick={() => act(n.id, "decline")}
                        data-testid={`decline-${n.id}`}
                        className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-1.5 border border-white/20 text-white/60 text-[10px] uppercase tracking-[0.2em] font-bold hover:border-[#FF0055]/40 hover:text-[#FF0055]"
                      >
                        <X size={12} weight="bold" /> {t.decline}
                      </button>
                    </div>
                  ) : (
                    <div className="mt-2 flex items-center justify-between">
                      <span className={`text-[10px] uppercase tracking-[0.25em] font-bold ${
                        n.status === "accepted" ? "text-[#00E5FF]" : "text-white/40"
                      }`}>
                        {n.status === "accepted" ? t.accepted : t.declined}
                      </span>
                      {n.status === "accepted" && n.tournament_id && (
                        <Link
                          to={`/tournaments/${n.tournament_id}`}
                          onClick={() => setOpen(false)}
                          className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#00E5FF] hover:underline"
                        >
                          → {t.bracket}
                        </Link>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
